import React from "react";
import { Link } from "react-router-dom";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPaperPlane,
} from "react-icons/fa";
import { SiZalo } from "react-icons/si";
import useFooterConfig from "../hooks/useFooterConfig";
import api from "../services/api";
import logo from "../assets/logo.png";

const Footer = () => {
  const { footerConfig } = useFooterConfig();
  const [policies, setPolicies] = React.useState([]);

  React.useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await api.get("/policies");
        if (response.data) {
          setPolicies(response.data);
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    };
    fetchPolicies();
  }, []);

  // Use dynamic data or fallbacks safely
  const contact = {
    description:
      footerConfig?.description ||
      "Hệ thống cửa hàng giày Sneaker chính hãng hàng đầu Việt Nam. Chúng tôi cam kết mang đến những sản phẩm chất lượng nhất và dịch vụ chăm sóc khách hàng tận tâm.",
    address:
      footerConfig?.address ||
      "44 Đ. 24, Linh Đông, Thủ Đức, Thành phố Hồ Chí Minh",
    mapsLink: footerConfig?.mapsLink || "",
    phone: footerConfig?.phone || "0765.109.784",
    workingHours: footerConfig?.workingHours || "08:30 - 22:00",
    zalo: footerConfig?.zalo || "0765.109.784",
    email: footerConfig?.email || "contact@tahashoes.vn",
    facebook: footerConfig?.facebook || "https://www.facebook.com/tahashoes.vn",
    instagram: footerConfig?.instagram || "#",
    tiktok: footerConfig?.tiktok || "#",
    youtube: footerConfig?.youtube || "#",
    businessName: footerConfig?.businessname || "",
    taxId: footerConfig?.taxid || "",
    issuedBy: footerConfig?.issuedby || "",
    issuedDate: footerConfig?.issueddate || "",
    showBusinessInfo: footerConfig?.showbusinessinfo !== undefined ? footerConfig.showbusinessinfo : true,
    categories:
      footerConfig?.categories?.length > 0
        ? footerConfig.categories
        : [
            { name: "Nike", href: "/products?brand=Nike" },
            { name: "Adidas", href: "/products?brand=Adidas" },
            { name: "Jordan Brand", href: "/products?brand=Jordan" },
            { name: "MLB Korea", href: "/products?brand=MLB" },
            { name: "Puma", href: "/products?brand=Puma" },
            { name: "Vans & Converse", href: "/products?brand=Vans" },
          ],
  };

  return (
    <footer
      className="bg-[#0a0f18] text-slate-400 pt-6 sm:pt-10 pb-4 sm:pb-6"
      style={{
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center",
      }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 mb-6">
          {/* Section 1: Brand Info & Widgets */}
          <div className="lg:col-span-1 space-y-8">
            <div className="space-y-6 flex flex-col items-start text-left">
              <Link to="/" className="flex items-center gap-2 group">
                <img
                  src={logo}
                  alt="TaHa"
                  className="h-8 sm:h-10 w-auto border-[1px] border-white rounded-full"
                />
                <span className="text-xl sm:text-2xl font-bold tracking-tight text-white">
                  TaHa <span className="text-red-600">Shoes</span>
                </span>
              </Link>
              <p className="text-xs sm:text-sm leading-relaxed max-w-sm">
                {contact.description}
              </p>
              <div className="flex items-center justify-start gap-4">
                {[
                  {
                    Icon: FaFacebookF,
                    color: "bg-blue-600",
                    href: contact.facebook,
                  },
                  {
                    Icon: FaInstagram,
                    color:
                      "bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]",
                    href: contact.instagram,
                  },
                  {
                    Icon: FaTiktok,
                    color: "bg-black",
                    href: contact.tiktok,
                  },
                  {
                    Icon: FaYoutube,
                    color: "bg-red-600",
                    href: contact.youtube,
                  },
                ].map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${social.color} flex items-center justify-center text-white transition-all duration-300 hover:scale-110 hover:-translate-y-1 shadow-lg shadow-black/20 border-[1px] border-white`}
                  >
                    <social.Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Section 2: Widgets Row (Side-by-side) */}
          <div className="lg:col-span-1">
            <div className="grid grid-cols-2 gap-4 h-full items-end pb-2">
              {/* Facebook Page Embed */}
              <div className="w-full bg-white rounded-2xl overflow-hidden border border-slate-800 h-[130px] relative">
                <div
                  className="absolute top-0 left-0 origin-top-left w-[118%] h-[118%]"
                  style={{ transform: "scale(0.85)" }}
                >
                  <iframe
                    src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(contact.facebook)}&tabs=&width=340&height=180&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false&appId`}
                    width="100%"
                    height="180"
                    style={{ border: "none", overflow: "hidden" }}
                    scrolling="no"
                    frameBorder="0"
                    allowFullScreen={true}
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    title="Facebook Page"
                  ></iframe>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="w-full bg-slate-800/20 rounded-2xl overflow-hidden border border-slate-800 h-[130px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3918.479624363927!2d106.73772427221999!3d10.85107813419278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3175277ff01d0059%3A0x5009f2a9adbbc880!2sTAHA%20SHOES!5e0!3m2!1svi!2s!4v1773418653463!5m2!1svi!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        {/* Links Grid Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 mb-6 border-t border-slate-800 pt-6">
          {/* Column : Categories */}
          <div className="flex flex-col items-start text-left">
            <h3 className="text-white font-bold text-base sm:text-lg mb-6 sm:mb-8 relative inline-block">
              Danh mục nổi bật
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-red-600 rounded-full"></span>
            </h3>
            <ul className="space-y-1.5 text-xs sm:text-sm">
              {contact.categories.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="hover:text-red-500 transition-colors flex items-center justify-start gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-700 group-hover:bg-red-500 transition-colors hidden sm:block"></span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 2: Policies */}
          <div className="flex flex-col items-start text-left">
            <h3 className="text-white font-bold text-base sm:text-lg mb-6 sm:mb-8 relative inline-block">
              Chính sách mua hàng
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-red-600 rounded-full"></span>
            </h3>
            <ul className="space-y-1.5 text-xs sm:text-sm">
              {policies.map((item) => (
                <li key={item._id}>
                  <Link
                    to={`/policy/${item.slug}`}
                    className="hover:text-red-500 transition-colors"
                  >
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Guide */}
          <div className="flex flex-col items-start text-left">
            <h3 className="text-white font-bold text-base sm:text-lg mb-6 sm:mb-8 relative inline-block">
              Hỗ trợ khách hàng
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-red-600 rounded-full"></span>
            </h3>
            <ul className="space-y-1.5 text-xs sm:text-sm">
              <li key="size">
                <Link
                  to="/guide/size"
                  className="hover:text-red-500 transition-colors"
                >
                  Hướng dẫn chọn size
                </Link>
              </li>
              <li key="care">
                <Link
                  to="/guide/care"
                  className="hover:text-red-500 transition-colors"
                >
                  Hướng dẫn sử dụng và bảo quản
                </Link>
              </li>
              <li key="orders">
                <Link
                  to="/orders"
                  className="hover:text-red-500 transition-colors"
                >
                  Kiểm tra đơn hàng
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div className="space-y-6 flex flex-col items-start text-left">
            <h3 className="text-white font-bold text-base sm:text-lg mb-0 sm:mb-2 relative inline-block">
              Thông tin liên hệ
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-red-600 rounded-full"></span>
            </h3>
            <ul className="space-y-1.5 text-xs sm:text-sm w-full">

              <li className="flex items-start justify-start gap-4 group/contact">
                <div className="w-8 h-8 rounded-full bg-[#EA4335] flex items-center justify-center text-white flex-shrink-0 transition-all duration-300 group-hover/contact:scale-110 shadow-lg shadow-red-500/50">
                  <FaMapMarkerAlt size={16} />
                </div>
                <a
                  href={
                    contact.mapsLink ||
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors pt-1 font-medium"
                >
                  {contact.address}
                </a>
              </li>
              <li className="flex items-center justify-start gap-4 group/contact">
                <div className="w-8 h-8 rounded-full bg-[#4CAF50] flex items-center justify-center text-white flex-shrink-0 transition-all duration-300 group-hover/contact:scale-110 shadow-lg shadow-green-500/50">
                  <FaPhoneAlt size={16} />
                </div>
                <a
                  href={`tel:${contact.phone}`}
                  className="hover:text-cyan-400 transition-colors font-medium"
                >
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-center justify-start gap-4 group/contact">
                <a
                  href={`https://zalo.me/${contact.zalo.replace(/\./g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 group/zalo"
                >
                  <div className="w-8 h-8 rounded-full bg-[#0068ff] flex items-center justify-center text-white flex-shrink-0 transition-all duration-300 group-hover/zalo:scale-110 shadow-lg shadow-blue-500/50">
                    <SiZalo size={18} />
                  </div>
                  <span className="hover:text-cyan-400 transition-colors font-medium">
                    Zalo: {contact.zalo}
                  </span>
                </a>
              </li>
            </ul>
          </div>

          {/* New Section: Business Info spanning 3 columns */}
          {contact.showBusinessInfo && contact.businessName && (
            <div className="sm:col-span-2 lg:col-span-3 flex flex-col items-start text-left pt-6 border-t border-slate-800/50 mt-4 sm:mt-0">
              <div className="text-xs sm:text-sm leading-relaxed space-y-1">
                <p className="font-bold text-slate-300 uppercase tracking-wider">
                  {contact.businessName}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 opacity-80">
                  <p>Mã số thuế: {contact.taxId}</p>
                  <p>Ngày cấp: {contact.issuedDate}</p>
                  <p className="sm:col-span-2">Nơi cấp: {contact.issuedBy}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-4 sm:pt-6 flex flex-col md:flex-row items-start justify-between gap-6 text-[10px] sm:text-xs tracking-wider text-left">
          <p>© {new Date().getFullYear()} TaHa Shoes. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">
              Quyền riêng tư
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Điều khoản
            </Link>
            <Link to="/sitemap" className="hover:text-white transition-colors">
              Sitemap
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg"
              className="h-3 opacity-30 grayscale hover:opacity-100 hover:grayscale-0 transition-all cursor-pointer"
              alt="Visa"
            />
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg"
              className="h-5 opacity-30 grayscale hover:opacity-100 hover:grayscale-0 transition-all cursor-pointer"
              alt="Mastercard"
            />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
